import {
  Component,
  Host,
  h,
  Prop,
  Watch,
  State,
  Event,
  EventEmitter,
  Listen,
  Element,
} from '@stencil/core';
import flatpickr from 'flatpickr';
import * as English from 'flatpickr/dist/l10n/default.js';
import * as Arabic from 'flatpickr/dist/l10n/ar.js';
import * as Bulgarian from 'flatpickr/dist/l10n/bg.js';
import * as Catalan from 'flatpickr/dist/l10n/cat.js';
import * as Mandarin from 'flatpickr/dist/l10n/zh.js';
import * as Croatian from 'flatpickr/dist/l10n/hr.js';
import * as Czech from 'flatpickr/dist/l10n/cs.js';
import * as Danish from 'flatpickr/dist/l10n/da.js';
import * as Dutch from 'flatpickr/dist/l10n/nl.js';
import * as Estonian from 'flatpickr/dist/l10n/et.js';
import * as Finnish from 'flatpickr/dist/l10n/fi.js';
import * as French from 'flatpickr/dist/l10n/fr.js';
import * as German from 'flatpickr/dist/l10n/de.js';
import * as Greek from 'flatpickr/dist/l10n/gr.js';
import * as Hebrew from 'flatpickr/dist/l10n/he.js';
import * as Hungarian from 'flatpickr/dist/l10n/hu.js';
import * as Icelandic from 'flatpickr/dist/l10n/is.js';
import * as Indonesian from 'flatpickr/dist/l10n/id.js';
import * as Italian from 'flatpickr/dist/l10n/it.js';
import * as Japanese from 'flatpickr/dist/l10n/ja.js';
import * as Korean from 'flatpickr/dist/l10n/ko.js';
import * as Latvian from 'flatpickr/dist/l10n/lv.js';
import * as Lithuanian from 'flatpickr/dist/l10n/lt.js';
import * as Malaysian from 'flatpickr/dist/l10n/ms.js';
import * as Norwegian from 'flatpickr/dist/l10n/no.js';
import * as Polish from 'flatpickr/dist/l10n/pl.js';
import * as Portuguese from 'flatpickr/dist/l10n/pt.js';
import * as Romanian from 'flatpickr/dist/l10n/ro.js';
import * as Russian from 'flatpickr/dist/l10n/ru.js';
import * as Serbian from 'flatpickr/dist/l10n/sr.js';
import * as Slovak from 'flatpickr/dist/l10n/sk.js';
import * as Slovenian from 'flatpickr/dist/l10n/sl.js';
import * as Spanish from 'flatpickr/dist/l10n/es.js';
import * as Swedish from 'flatpickr/dist/l10n/sv.js';
import * as Thai from 'flatpickr/dist/l10n/th.js';
import * as Turkish from 'flatpickr/dist/l10n/tr.js';
import * as Ukrainian from 'flatpickr/dist/l10n/uk.js';
import * as Vietnamese from 'flatpickr/dist/l10n/vn.js';
import {
  Language,
  QwLanguageKeys,
  SessionDisplay,
  SessionStayPeriod,
} from '@cesare';
import { QwLocation } from 'apps/jo-joe/src';

const CALENDAR_ID = 'flatpickr-element';

German.default.de.weekdays = {
  shorthand: ['Son', 'Mon', 'Die', 'Mit', 'Don', 'Fre', 'Sam'],
  longhand: German.default.de.weekdays.longhand,
};
Hungarian.default.hu.weekdays = {
  shorthand: ['Vas', 'Hét', 'Ked', 'Sze', 'Csü', 'Pén', 'Szo'],
  longhand: Hungarian.default.de.weekdays.longhand,
};
interface DatePeriod {
  from: string;
  to: string;
}

export interface OverlapResult {
  isOverlapping: boolean;
  nextAvailablePeriod: {
    from: string;
    to: string;
  };
}

@Component({
  tag: 'qw-calendar-picker',
  styleUrl: 'qw-calendar-picker.scss',
  shadow: false,
})
export class QwCalendarPicker {
  @Element() el: HTMLElement;
  @Prop() qwCalendarPickerId: string = CALENDAR_ID;
  @Prop() qwCalendarPickerDisabled: boolean;
  @Prop() qwCalendarPickerNumberOfMonths: number;
  @Prop() qwCalendarPickerResponsive: boolean;
  @Prop() qwCalendarPickerStayPeriod: SessionStayPeriod;
  @Prop() qwCalendarPickerDesktopLimit: number;
  @Prop() qwCalendarPickerLocale: SessionDisplay['culture'];
  @Prop() qwCalendarPickerConfig: any; // todo capire perché si rompe flatpickr.Options.Options;
  @Prop() qwCalendarPickerLocations: any; // todo capire perché si rompe flatpickr.Options.Options;
  @State() qwCalendarPickerDisableStayPeriod: { from: string; to: string };
  @State() disableStartDate: boolean = false;
  @State() calendarInstance: flatpickr.Instance;
  @Event() qwCalendarPickerChangeDates: EventEmitter<SessionStayPeriod>;
  @Event() qwCalendarPickerChangeDatesOpenCalendar: EventEmitter<{
    stayPeriod: SessionStayPeriod;
    relativeCalendar?: any;
    openCalendar?: boolean;
  }>;

  private elementCalendarInstance: HTMLElement;
  private configCalendarInstance: { [key: string]: any } = {};

  public componentDidLoad() {
    this.initCalendar();
    this.qwCalendarPickerConfig.defaultDate &&
      this.updateConfigCalendarAndRefresh({
        defaultDate: [
          this.qwCalendarPickerConfig.defaultDate[0],
          this.qwCalendarPickerConfig.defaultDate[1],
        ],
      });
  }

  private initCalendar() {
    this.elementCalendarInstance = document.querySelector(
      `#${this.qwCalendarPickerId}`,
    );

    if (this.qwCalendarPickerLocations) {
      const selectedLoaction = this.qwCalendarPickerLocations.hostel.find(
        (item) => item.is_current === true,
      );

      this.qwCalendarPickerDisableStayPeriod =
        selectedLoaction.disable_stay_period != false
          ? selectedLoaction.disable_stay_period
          : null;
    }

    this.configCalendarInstance = {
      mode: 'range',
      minDate: 'today',
      inline: true,
      showMonths: this.qwCalendarPickerNumberOfMonths,
      locale: {
        ...this.getLocaleForCalendar(),
        firstDayOfWeek: 1,
      },
      ...(this.qwCalendarPickerDisableStayPeriod
        ? { disable: [this.qwCalendarPickerDisableStayPeriod] }
        : {}),
      onChange: this.change,
      onDayCreate: function (_dObj, _dStr, _fp, dayElem) {
        if (dayElem.classList.contains('flatpickr-disabled')) {
          dayElem.setAttribute(
            'title',
            Language.getTranslation('invalidDates'),
          );
        }
      },
      ...this.qwCalendarPickerConfig,
    };
    this.calendarInstance = flatpickr(
      this.elementCalendarInstance,
      this.configCalendarInstance,
    );
    this.setOneOrTwoMonthIfResponsive();
  }

  change = (selectedDates: Date[], dateStr: string) => {
    if (selectedDates.length === 1) {
      this.disableStartDate = true;
    }

    if (selectedDates.length === 2) {
      this.disableStartDate = false;
      const datesToEmit = dateStr.split(' ');
      const newStayPeriod: SessionStayPeriod = {
        arrivalDate: datesToEmit[0],
        departureDate: datesToEmit[datesToEmit.length - 1] || datesToEmit[0],
      };
      this.qwCalendarPickerChangeDates.emit(newStayPeriod);
    }
  };

  private updateConfigCalendarAndRefresh(options) {
    this.configCalendarInstance = {
      ...this.configCalendarInstance,
      ...options,
    };
    this.calendarInstance = this.getInstance();
  }

  private getInstance() {
    return flatpickr(this.elementCalendarInstance, this.configCalendarInstance);
  }

  private setOneOrTwoMonthIfResponsive() {
    if (!this.qwCalendarPickerResponsive) {
      return;
    }

    const width = window.innerWidth;
    if (
      width > this.qwCalendarPickerDesktopLimit &&
      this.qwCalendarPickerNumberOfMonths === 1
    ) {
      this.qwCalendarPickerNumberOfMonths = 2;
      this.updateConfigCalendarAndRefresh({
        showMonths: this.qwCalendarPickerNumberOfMonths,
      });
    }

    if (
      width <= this.qwCalendarPickerDesktopLimit &&
      this.qwCalendarPickerNumberOfMonths === 2
    ) {
      this.qwCalendarPickerNumberOfMonths = 1;
      this.updateConfigCalendarAndRefresh({
        showMonths: this.qwCalendarPickerNumberOfMonths,
      });
    }
  }

  public checkStayPeriodsOverlap(
    currentStayPeriod: DatePeriod,
    disableStayPeriod: DatePeriod,
  ): OverlapResult {
    const today = new Date();

    const disableFrom = new Date(disableStayPeriod.from);
    const disableTo = new Date(disableStayPeriod.to);
    const currentFrom = new Date(currentStayPeriod.from);
    const currentTo = new Date(currentStayPeriod.to);

    const isOverlapping = disableFrom <= currentTo && currentFrom <= disableTo;
    const isDisablePeriodFuture = disableFrom > today;
    const disableFutureRange = !isOverlapping && isDisablePeriodFuture;

    if (disableFutureRange) {
      return {
        isOverlapping,
        nextAvailablePeriod: currentStayPeriod,
      };
    }

    let nextAvailableFrom: Date;
    let nextAvailableTo: Date;

    if (isOverlapping) {
      const daysBeforeDisabled = Math.floor(
        (disableFrom.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
      );

      if (daysBeforeDisabled >= 2) {
        nextAvailableFrom = new Date(today);
        nextAvailableTo = new Date(today);
        nextAvailableTo.setDate(nextAvailableTo.getDate() + 1);
      } else {
        nextAvailableFrom = new Date(disableTo);
        nextAvailableFrom.setDate(nextAvailableFrom.getDate() + 1);

        nextAvailableTo = new Date(nextAvailableFrom);
        nextAvailableTo.setDate(nextAvailableTo.getDate() + 1);
      }
    } else {
      nextAvailableFrom = new Date(currentFrom);
      nextAvailableTo = new Date(currentTo);
    }

    const formattedFromDate = nextAvailableFrom.toISOString().split('T')[0];
    const formattedToDate = nextAvailableTo.toISOString().split('T')[0];

    return {
      isOverlapping,
      nextAvailablePeriod: {
        from: formattedFromDate,
        to: formattedToDate,
      },
    };
  }

  private getLocaleForCalendar() {
    this.qwCalendarPickerLocale =
      this.qwCalendarPickerLocale == 'pt-br'
        ? 'pt-PT'
        : this.qwCalendarPickerLocale;

    switch (this.qwCalendarPickerLocale) {
      case QwLanguageKeys.ar:
        return Arabic.default.ar;
      case QwLanguageKeys.bg:
        return Bulgarian.default.bg;
      case QwLanguageKeys.ca:
        return Catalan.default.ca;
      case QwLanguageKeys.zh:
        return Mandarin.default.zh;
      case QwLanguageKeys.hr:
        return Croatian.default.hr;
      case QwLanguageKeys.cs:
        return Czech.default.cs;
      case QwLanguageKeys.da:
        return Danish.default.da;
      case QwLanguageKeys.nl:
        return Dutch.default.nl;
      case QwLanguageKeys.et:
        return Estonian.default.et;
      case QwLanguageKeys.fi:
        return Finnish.default.fi;
      case QwLanguageKeys.fr:
        return French.default.fr;
      case QwLanguageKeys.de:
        return German.default.de;
      case QwLanguageKeys.el:
        return Greek.default.gr;
      case QwLanguageKeys.he:
        return Hebrew.default.he;
      case QwLanguageKeys.hu:
        return Hungarian.default.hu;
      case QwLanguageKeys.is:
        return Icelandic.default.is;
      case QwLanguageKeys.id:
        return Indonesian.default.id;
      case QwLanguageKeys.it:
        return Italian.default.it;
      case QwLanguageKeys.ja:
        return Japanese.default.ja;
      case QwLanguageKeys.ko:
        return Korean.default.ko;
      case QwLanguageKeys.lv:
        return Latvian.default.lv;
      case QwLanguageKeys.lt:
        return Lithuanian.default.lt;
      case QwLanguageKeys.ms:
        return Malaysian.default.ms;
      case QwLanguageKeys.nb:
        return Norwegian.default.no;
      case QwLanguageKeys.pl:
        return Polish.default.pl;
      case QwLanguageKeys.pt:
        return Portuguese.default.pt;
      case QwLanguageKeys.ro:
        return Romanian.default.ro;
      case QwLanguageKeys.ru:
        return Russian.default.ru;
      case QwLanguageKeys.sr:
        return Serbian.default.sr;
      case QwLanguageKeys.sk:
        return Slovak.default.sk;
      case QwLanguageKeys.sl:
        return Slovenian.default.sl;
      case QwLanguageKeys.es:
        return Spanish.default.es;
      case QwLanguageKeys.sv:
        return Swedish.default.sv;
      case QwLanguageKeys.th:
        return Thai.default.th;
      case QwLanguageKeys.tr:
        return Turkish.default.tr;
      case QwLanguageKeys.uk:
        return Ukrainian.default.uk;
      case QwLanguageKeys.vi:
        return Vietnamese.default.vn;
      default:
        return English.default;
    }
  }

  @Listen('changeLocationLink', { target: 'window' })
  public checkDisablePeriods(location: QwLocation | any) {
    const selectedLocation = this.qwCalendarPickerLocations.hostel.find(
      (locationData) => locationData.value === location.detail.value,
    );

    const disabledStayPeriod = selectedLocation?.disable_stay_period;
    const defaultArrival = this.get7DaysFromNow();
    const defaultDeparture = this.get8DaysFromNow();
    const currentDates = {
      from: this.qwCalendarPickerStayPeriod.arrivalDate,
      to: this.qwCalendarPickerStayPeriod.departureDate,
    };

    // No disable dates, reset
    if (!disabledStayPeriod || Object.keys(disabledStayPeriod).length === 0) {
      if (this.configCalendarInstance.disable) {
        this.handleDateUpdate(defaultArrival, defaultDeparture, []);
      }
      return;
    }

    const currentOverlap = this.checkStayPeriodsOverlap(
      currentDates,
      disabledStayPeriod,
    );

    if (currentOverlap.isOverlapping) {
      // Current dates overlap - use next available and open calendar
      const defaultDatesAreAvailable = this.checkStayPeriodsOverlap(
        { from: defaultArrival, to: defaultDeparture },
        disabledStayPeriod,
      );
      let opencalendar = true;
      if (!defaultDatesAreAvailable.isOverlapping) {
        currentOverlap.nextAvailablePeriod.from = defaultArrival;
        currentOverlap.nextAvailablePeriod.to = defaultDeparture;
        opencalendar = false;
      }

      this.emitDateChange(
        currentOverlap.nextAvailablePeriod.from,
        currentOverlap.nextAvailablePeriod.to,
        opencalendar,
      );
      this.updateConfigCalendarAndRefresh({
        defaultDate: [
          currentOverlap.nextAvailablePeriod.from,
          currentOverlap.nextAvailablePeriod.to,
        ],
        disable: [disabledStayPeriod],
      });
      return;
    }

    // Current dates don't overlap - find best available dates
    const bestDates = this.findBestAvailableDates(
      { from: defaultArrival, to: defaultDeparture },
      currentDates,
      disabledStayPeriod,
    );

    if (bestDates && this.shouldUpdateDates(bestDates, currentDates)) {
      this.emitDateChange(bestDates.from, bestDates.to, false);
      this.updateConfigCalendarAndRefresh({
        defaultDate: [bestDates.from, bestDates.to],
        disable: [disabledStayPeriod],
      });
    } else {
      // Keep current dates, just update disable period
      this.updateConfigCalendarAndRefresh({
        disable: [disabledStayPeriod],
      });
    }
  }

  private findBestAvailableDates(
    defaultDates: DatePeriod,
    currentDates: DatePeriod,
    disabledPeriod: DatePeriod,
  ): DatePeriod {
    // 1. Try default dates first
    const defaultOverlap = this.checkStayPeriodsOverlap(
      defaultDates,
      disabledPeriod,
    );
    if (!defaultOverlap.isOverlapping) {
      return defaultDates;
    }

    // 2. Try to find earlier available dates
    return this.findEarlierAvailableDates(
      defaultDates,
      currentDates,
      disabledPeriod,
    );
  }

  private shouldUpdateDates(
    newDates: DatePeriod,
    currentDates: DatePeriod,
  ): boolean {
    return (
      newDates.from !== currentDates.from || newDates.to !== currentDates.to
    );
  }

  private emitDateChange(
    arrivalDate: string,
    departureDate: string,
    openCalendar: boolean = false,
  ) {
    const relativeCalendar = openCalendar
      ? this.el?.closest('qw-booking-bar')?.querySelector('qw-calendar') ||
        this.el?.closest('qw-update-search')?.querySelector('qw-calendar')
      : null;

    this.qwCalendarPickerChangeDatesOpenCalendar.emit({
      stayPeriod: { arrivalDate, departureDate },
      relativeCalendar,
      openCalendar,
    });
  }

  private handleDateUpdate(
    defaultArrival: string,
    defaultDeparture: string,
    disable: any[],
  ) {
    const shouldUpdateDates =
      defaultArrival !== this.qwCalendarPickerStayPeriod.arrivalDate ||
      defaultDeparture !== this.qwCalendarPickerStayPeriod.departureDate;

    this.updateConfigCalendarAndRefresh({
      ...(shouldUpdateDates
        ? { defaultDate: [defaultArrival, defaultDeparture] }
        : {}),
      disable,
    });

    if (shouldUpdateDates) {
      this.emitDateChange(defaultArrival, defaultDeparture);
    }
  }

  private findEarlierAvailableDates(
    defaultDates: DatePeriod,
    currentDates: DatePeriod,
    disabledPeriod: DatePeriod,
  ): DatePeriod | null {
    const today = new Date();
    const disableFrom = new Date(disabledPeriod.from);
    const disableTo = new Date(disabledPeriod.to);
    const stayDuration = this.calculateStayDuration(currentDates);

    const daysBeforeDisabled = Math.floor(
      (disableFrom.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );

    // Try dates BEFORE disabled period
    if (daysBeforeDisabled >= stayDuration + 1) {
      // Try default dates first if they fit
      const defaultFrom = new Date(defaultDates.from);
      const defaultTo = new Date(defaultDates.to);
      if (defaultTo < disableFrom && defaultFrom >= today) {
        return defaultDates;
      }

      // Find earliest possible dates before disabled period
      const earliestFrom = new Date(today);
      earliestFrom.setDate(today.getDate() + 1);
      const earliestTo = new Date(earliestFrom);
      earliestTo.setDate(earliestFrom.getDate() + stayDuration);

      if (earliestTo < disableFrom) {
        return {
          from: earliestFrom.toISOString().split('T')[0],
          to: earliestTo.toISOString().split('T')[0],
        };
      }
    }

    // No space before disabled period, get first available AFTER disabled period
    const afterDisabledFrom = new Date(disableTo);
    afterDisabledFrom.setDate(afterDisabledFrom.getDate() + 1);
    const afterDisabledTo = new Date(afterDisabledFrom);
    afterDisabledTo.setDate(afterDisabledFrom.getDate() + stayDuration);

    return {
      from: afterDisabledFrom.toISOString().split('T')[0],
      to: afterDisabledTo.toISOString().split('T')[0],
    };
  }
  private calculateStayDuration(dates: DatePeriod): number {
    const from = new Date(dates.from);
    const to = new Date(dates.to);
    return Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
  }

  private get7DaysFromNow() {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  }

  private get8DaysFromNow() {
    const date = new Date();
    date.setDate(date.getDate() + 8);
    return date.toISOString().split('T')[0];
  }

  @Watch('qwCalendarPickerStayPeriod')
  watchStayPeriod(newValue: SessionStayPeriod) {
    this.updateConfigCalendarAndRefresh({
      defaultDate: [newValue.arrivalDate, newValue.departureDate],
    });
  }

  @Watch('qwCalendarPickerLocale')
  watchLocale() {
    this.updateConfigCalendarAndRefresh({
      locale: this.getLocaleForCalendar(),
    });
  }

  @Listen('resize', { target: 'window' })
  handleScroll() {
    this.setOneOrTwoMonthIfResponsive();
  }

  render() {
    return (
      <Host
        class={`
        ${this.qwCalendarPickerDisabled && 'qw-calendar-picker--disabled'}
        ${this.disableStartDate && 'qw-calendar-picker--disable-start-date'}
        ${this.qwCalendarPickerResponsive && 'qw-calendar-picker--responsive'}
      `}
      >
        <div id={this.qwCalendarPickerId} />
      </Host>
    );
  }
}
